import { useState, useRef, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../redux";
import { bugTicketActions } from "../redux/reducers/bugTicketSlice";
import { Button, Dialog, InputField, View, Text } from "./base";
import { Icon } from "./base/Icon";

type SubmitBugDialogProps = {
  isOpen?: boolean;
  onClose?: () => void;
};

export const SubmitBugDialog = ({ isOpen = false, onClose = () => {} }: SubmitBugDialogProps) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.bugTicket?.isLoadingCreate || false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setScreenshot(null);
    setScreenshotPreview(null);
    setSubmitted(false);
    setTicketId(null);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Screenshot must be less than 5MB");
        return;
      }
      setScreenshot(file);
      setError(null);
    }
  };

  // Create preview when screenshot changes
  useEffect(() => {
    if (!screenshot) {
      setScreenshotPreview(null);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshotPreview(reader.result as string);
    };
    reader.onerror = () => {
      setError("Failed to read image file");
      setScreenshotPreview(null);
    };
    reader.readAsDataURL(screenshot);

    return () => {
      reader.abort();
    };
  }, [screenshot]);

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    // Validate
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!description.trim()) {
      setError("Description is required");
      return;
    }

    setError(null);

    let screenshotUrl: string | undefined;

    // Upload screenshot if provided
    if (screenshot) {
      try {
        const formData = new FormData();
        formData.append("file", screenshot);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/files`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Upload failed: ${response.status}`);
        }

        const data = await response.json();
        screenshotUrl = data.data?.url;
      } catch (err: any) {
        console.error("Failed to upload screenshot:", err);
        setError(err?.message || "Failed to upload screenshot. Please try again without it.");
        return;
      }
    }

    // Submit bug ticket
    const result = await dispatch(
      bugTicketActions.submitBugTicket({
        title: title.trim(),
        description: description.trim(),
        screenshotUrl,
      }),
    );

    if (bugTicketActions.submitBugTicket.fulfilled.match(result)) {
      setTicketId(result.payload.id);
      setSubmitted(true);
    } else {
      setError((result.payload as string) || "Failed to submit bug report. Please try again.");
    }
  };

  // Success state
  if (submitted && ticketId) {
    return (
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        title="Bug Report Submitted"
        className="w-full max-w-md"
      >
        <View className="flex flex-col items-center p-6 space-y-4">
          <View className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
            <Icon
              name="check"
              size={32}
              className="text-green-600 dark:text-green-400"
            />
          </View>

          <View className="flex flex-col items-center space-y-2">
            <Text className="text-lg font-semibold text-gray-900 dark:text-white">Thank you!</Text>
            <Text className="text-gray-600 dark:text-gray-400">Your bug report has been submitted successfully.</Text>
            <View className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mt-4 w-full text-center">
              <Text className="text-sm text-gray-500 dark:text-gray-400">Ticket ID:</Text>
              <Text className="text-base font-mono font-medium text-gray-900 dark:text-white">{ticketId}</Text>
            </View>
          </View>

          <Button
            variant="brand"
            onClick={handleClose}
            className="w-full mt-2"
          >
            Close
          </Button>
        </View>
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Submit Bug Report"
      className="w-full max-w-lg"
    >
      <View className="flex flex-col overflow-y-auto max-h-[80vh] p-4 space-y-4">
        {error && (
          <View className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <Text className="text-sm text-red-600 dark:text-red-400">{error}</Text>
          </View>
        )}

        <InputField
          label="Title *"
          value={title}
          onChange={setTitle}
          placeholder="Brief description of the issue"
          isDisabled={isLoading}
        />

        <InputField
          label="Description *"
          type="textarea"
          value={description}
          onChange={setDescription}
          placeholder="Please describe the bug in detail. What did you expect to happen? What actually happened?"
          isDisabled={isLoading}
        />

        <View className="space-y-2">
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Screenshot (optional)</Text>

          {!screenshotPreview ? (
            <View
              className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon
                name="image"
                size={24}
                className="text-gray-400 mx-auto mb-2"
              />
              <Text className="text-sm text-gray-500 dark:text-gray-400">Click to upload screenshot</Text>
              <Text className="text-xs text-gray-400 dark:text-gray-500 mt-1">Max 5MB, images only</Text>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleScreenshotChange}
                className="hidden"
              />
            </View>
          ) : (
            <View className="relative">
              <img
                src={screenshotPreview}
                alt="Screenshot preview"
                className="w-full h-40 object-cover rounded-lg"
              />
              <button
                onClick={handleRemoveScreenshot}
                className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                <Icon
                  name="close"
                  size={14}
                />
              </button>
            </View>
          )}
        </View>

        <View className="flex flex-row items-center justify-end gap-2 pt-2">
          <Button
            variant="neutral"
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleSubmit}
            isDisabled={isLoading || !title.trim() || !description.trim()}
          >
            {isLoading ? (
              <>
                <Icon
                  name="refresh"
                  size={16}
                  className="animate-spin mr-2"
                />
                Submitting...
              </>
            ) : (
              <>
                <Icon
                  name="send"
                  size={16}
                  className="mr-2"
                />
                Submit Bug Report
              </>
            )}
          </Button>
        </View>
      </View>
    </Dialog>
  );
};
